import React, { useEffect, useMemo, useState } from "react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";
import { Medicine } from "../medicines/types";

interface MedicineGroup {
  key: string;
  name: string;
  brand?: string;
  manufacturer?: string;
  ids: string[];
  batchCount: number;
  totalStock: number;
  minimumStockCount: number;
}

const isMedicine = (item: Medicine) => {
  const type = String(item.type || "").toUpperCase();
  return type !== "SERVICE" && type !== "LAB";
};

const groupMedicines = (items: Medicine[]): MedicineGroup[] => {
  const groups = new Map<string, MedicineGroup>();

  items.filter(isMedicine).forEach((item) => {
    const name = String(item.name || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    const existing = groups.get(key);
    if (existing) {
      existing.ids.push(item.id);
      existing.batchCount += 1;
      existing.totalStock += Number(item.quantity || 0);
      existing.minimumStockCount = Number(item.minimum_stock_count ?? existing.minimumStockCount ?? 10);
      return;
    }

    groups.set(key, {
      key,
      name,
      brand: item.brand,
      manufacturer: item.manufacturer,
      ids: [item.id],
      batchCount: 1,
      totalStock: Number(item.quantity || 0),
      minimumStockCount: Number(item.minimum_stock_count ?? 10),
    });
  });

  return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const MedicineMinimumStockConfig: React.FC = () => {
  const [groups, setGroups] = useState<MedicineGroup[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });

  const loadMedicines = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/medicines`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      const nextGroups = groupMedicines(data || []);
      setGroups(nextGroups);
      setValues(
        nextGroups.reduce((next: Record<string, string>, group) => {
          next[group.key] = String(group.minimumStockCount || 0);
          return next;
        }, {})
      );
    } catch (err: any) {
      setModal({ open: true, title: "Unable to load medicines", message: err.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMedicines();
  }, []);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter((group) =>
      [group.name, group.brand, group.manufacturer]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [groups, search]);

  const saveMinimum = async (group: MedicineGroup) => {
    const minimumStockCount = Number(values[group.key] || 0);
    if (!Number.isFinite(minimumStockCount) || minimumStockCount < 0) {
      setModal({ open: true, title: "Invalid minimum stock", message: "Enter zero or a positive minimum stock count." });
      return;
    }

    setSavingKey(group.key);
    try {
      await Promise.all(
        group.ids.map((id) =>
          fetch(`${BACKEND_URL}/api/medicines/${id}`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ minimum_stock_count: minimumStockCount }),
          }).then(async (response) => {
            if (!response.ok) throw new Error(await response.text());
          })
        )
      );

      setGroups((prev) =>
        prev.map((item) =>
          item.key === group.key ? { ...item, minimumStockCount } : item
        )
      );
      setModal({ open: true, title: "Minimum stock updated", message: `${group.name} minimum stock count has been saved.` });
    } catch (err: any) {
      setModal({ open: true, title: "Unable to save minimum stock", message: err.message || "Please try again." });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <SidebarLayout title="Minimum Stock Configuration" subtitle="Set minimum stock count for each unique medicine.">
      <Modal open={modal.open} title={modal.title} message={modal.message} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />

      <section className="rounded border border-border-light bg-surface-light p-4">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded border border-border-light p-2 sm:max-w-md"
            placeholder="Search medicine, brand, supplier"
          />
          <div className="text-sm font-semibold text-subtext-light">{filteredGroups.length} medicines</div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-subtext-light">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="bg-background-light text-xs uppercase text-subtext-light">
                <tr>
                  <th className="p-2">Medicine</th>
                  <th className="p-2">Brand</th>
                  <th className="p-2">Supplier</th>
                  <th className="p-2 text-right">Batches</th>
                  <th className="p-2 text-right">Total Stock</th>
                  <th className="p-2">Minimum Stock</th>
                  <th className="p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((group) => (
                  <tr key={group.key} className="border-t">
                    <td className="p-2 font-semibold">{group.name}</td>
                    <td className="p-2">{group.brand || "-"}</td>
                    <td className="p-2">{group.manufacturer || "-"}</td>
                    <td className="p-2 text-right">{group.batchCount}</td>
                    <td className="p-2 text-right font-bold">{group.totalStock}</td>
                    <td className="p-2">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={values[group.key] || ""}
                        onChange={(event) => setValues((prev) => ({ ...prev, [group.key]: event.target.value }))}
                        className="w-32 rounded border border-border-light p-2"
                      />
                    </td>
                    <td className="p-2">
                      <button
                        type="button"
                        onClick={() => saveMinimum(group)}
                        disabled={savingKey === group.key}
                        className="rounded bg-primary px-3 py-2 font-semibold text-white disabled:opacity-50"
                      >
                        {savingKey === group.key ? "Saving..." : "Save"}
                      </button>
                    </td>
                  </tr>
                ))}
                {!filteredGroups.length && (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-subtext-light">
                      No medicines found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </SidebarLayout>
  );
};

export default MedicineMinimumStockConfig;
